import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, MapPin, Phone, ShieldCheck } from "lucide-react";

export function useBusiness() {
  const [b, setB] = useState({});
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/business`).then(r => setB(r.data)).catch(() => {}); }, []);
  return b;
}

export function BusinessFooter({ className = "landing-footer" }) {
  const b = useBusiness();
  const owner = b.legal_name && b.legal_name !== "FeeMitra" ? b.legal_name : "";
  return <footer className={`${className} biz-footer`} data-testid="business-footer">
    <div className="biz-col">
      <b><ShieldCheck size={15} /> FeeMitra{owner && <span data-testid="footer-legal-name"> — a product of {owner}</span>}</b>
      {b.address && <span data-testid="footer-address"><MapPin size={13} /> {b.address}{b.state ? `, ${b.state}` : ""}</span>}
      {b.gstin && <span data-testid="footer-gstin">GSTIN: {b.gstin}</span>}
    </div>
    <div className="biz-col">
      {b.phone && <a href={`tel:${b.phone}`} data-testid="footer-phone"><Phone size={13} /> {b.phone}</a>}
      {b.email && <a href={`mailto:${b.email}`} data-testid="footer-email"><Mail size={13} /> {b.email}</a>}
      <span className="biz-links"><a href="/about" data-testid="footer-about">About</a> · <a href="/contact" data-testid="footer-contact">Contact</a> · <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a> · <a href="/refund">Refund</a></span>
      <span>© {new Date().getFullYear()} {owner || "FeeMitra"}</span>
    </div>
  </footer>;
}

function LegalShell({ title, lastUpdated, children }) {
  return <div className="legal-shell">
    <header className="legal-nav">
      <a href="/pricing" className="legal-brand"><ShieldCheck size={20} /> FeeMitra</a>
      <nav><a href="/pricing">Pricing</a><a href="/about">About</a><a href="/contact">Contact</a><a href="/terms">Terms</a><a href="/privacy">Privacy</a><a href="/refund">Refund</a><a href="/" className="legal-login">Sign in</a></nav>
    </header>
    <article className="legal-article">
      <p className="eyebrow">LEGAL</p>
      <h1>{title}</h1>
      <p className="muted">Last updated: {lastUpdated}</p>
      <div className="legal-body">{children}</div>
    </article>
    <BusinessFooter />
  </div>;
}

export function TermsPage() {
  return <LegalShell title="Terms of Service" lastUpdated="08 October 2026">
    <h2>1. Who we are</h2>
    <p>FeeMitra ("we", "us") provides software-as-a-service for Indian schools to manage fee collection, student records, and payments. By creating an account or using the Service, you accept these Terms.</p>
    <h2>2. Who can use the Service</h2>
    <p>The Service is available to legally registered schools, their authorised administrators and staff, and parents or guardians invited by a school. You must be at least 18 years old to create an admin account. Parents under 18 may use the Service only through a verified guardian.</p>
    <h2>3. Your school's data</h2>
    <p>You own all student, parent and payment data you upload. We only access it to deliver the Service, respond to a support request you file, or comply with Indian law. Data lives in encrypted databases in Indian or Singapore regions.</p>
    <h2>4. Acceptable use</h2>
    <p>Do not upload illegal content, attempt to break the Service, impersonate another school, or share your admin credentials with unauthorised staff. We can suspend accounts that violate these rules.</p>
    <h2>5. Fees and billing</h2>
    <p>Pricing is published on the <a href="/pricing">Pricing</a> page. Fees are billed per school in Indian Rupees. GST is added only where applicable and shown on the invoice. All sales are subject to our <a href="/refund">Refund Policy</a>.</p>
    <h2>6. Payment gateway</h2>
    <p>If you connect a Razorpay (or similar) account to collect parent payments, that money moves directly between the parent and your school's bank account. FeeMitra never holds parent funds. Gateway fees are set by the gateway, not by us.</p>
    <h2>7. Service availability</h2>
    <p>We target 99.5% monthly uptime. Scheduled maintenance is announced in advance. We're not liable for outages caused by third-party providers (payment gateways, SMS/WhatsApp providers, DNS, cloud infrastructure).</p>
    <h2>8. Termination</h2>
    <p>You can cancel at any time from Settings. We can terminate accounts for non-payment, abuse, or legal reasons with 14 days' notice. On termination we export your data in CSV format and delete it within 60 days.</p>
    <h2>9. Limitation of liability</h2>
    <p>Our total liability for any claim is limited to the fees you paid in the previous 12 months. We are not liable for indirect, incidental, consequential or punitive damages.</p>
    <h2>10. Governing law</h2>
    <p>These Terms are governed by the laws of India. Disputes will be resolved exclusively in courts in Kanpur, Uttar Pradesh.</p>
    <h2>11. Contact</h2>
    <p>Questions? Email <a href="mailto:support@feemitra.in">support@feemitra.in</a>.</p>
  </LegalShell>;
}

export function PrivacyPage() {
  return <LegalShell title="Privacy Policy" lastUpdated="08 October 2026">
    <h2>1. What we collect</h2>
    <ul>
      <li><b>School admin & staff</b>: name, email, role, hashed password, audit logs of actions.</li>
      <li><b>Students & parents</b>: name, admission number, class, parent mobile, email, address — only the fields the school chooses to upload.</li>
      <li><b>Payments</b>: amount, mode, receipt number and transaction ID. We never store full card numbers, CVV or UPI PINs.</li>
      <li><b>Usage data</b>: anonymised page views and performance metrics.</li>
    </ul>
    <h2>2. Why we collect it</h2>
    <p>To run the Service, generate receipts, verify payments, send fee reminders you configure, and respond to support requests.</p>
    <h2>3. Who we share it with</h2>
    <p>We never sell your data. We share limited data with (a) the payment gateway you choose, to process payments; (b) the WhatsApp or SMS provider you choose, to send reminders you configure; (c) Indian law enforcement, only if we are legally compelled.</p>
    <h2>4. Where it lives</h2>
    <p>Data is stored on managed MongoDB infrastructure in Indian / Singapore regions, encrypted at rest and in transit. Backups are retained for 30 days.</p>
    <h2>5. How long we keep it</h2>
    <p>While your account is active, plus 60 days after cancellation. After that we delete personally identifiable data and keep only anonymised aggregates. Receipts are kept for 7 years to meet Indian tax rules.</p>
    <h2>6. Your rights under DPDP Act 2023</h2>
    <p>You can access, correct, download or delete your data from Settings → Data. Parents can request their child's data from the school admin. Write to <a href="mailto:privacy@feemitra.in">privacy@feemitra.in</a> for anything else.</p>
    <h2>7. Cookies</h2>
    <p>We use one essential cookie to keep you signed in. No tracking cookies, no third-party analytics.</p>
    <h2>8. Children's data</h2>
    <p>Student data is uploaded by the school under the parent's consent recorded at admission. We only act as a processor on behalf of the school.</p>
    <h2>9. Changes</h2>
    <p>If we materially change this Policy we'll email every admin at least 14 days in advance.</p>
    <h2>10. Contact</h2>
    <p>Data Protection Officer: <a href="mailto:privacy@feemitra.in">privacy@feemitra.in</a>.</p>
  </LegalShell>;
}

export function RefundPage() {
  return <LegalShell title="Refund Policy" lastUpdated="08 October 2026">
    <h2>1. 30-day money-back guarantee</h2>
    <p>If FeeMitra doesn't work for your school, write to <a href="mailto:support@feemitra.in">support@feemitra.in</a> within 30 days of your first paid invoice and we will refund your subscription fee in full. No questions asked.</p>
    <h2>2. Pro-rated refunds</h2>
    <p>After 30 days, if you cancel mid-cycle we refund the unused days of the current month on a pro-rata basis. Setup fees are non-refundable after onboarding is complete.</p>
    <h2>3. Payment gateway fees</h2>
    <p>Transaction fees charged by Razorpay or your other gateway are set and refunded by that gateway, not by FeeMitra. We can help you file a chargeback dispute with the gateway.</p>
    <h2>4. Parent payment refunds</h2>
    <p>If a parent needs a refund of a fee they paid, the school admin initiates it from Payments → Refund. The gateway returns the amount to the original source within 5–7 working days. FeeMitra charges no refund fee.</p>
    <h2>5. How to request a refund</h2>
    <ol>
      <li>Email <a href="mailto:support@feemitra.in">support@feemitra.in</a> from the admin email on file.</li>
      <li>Include your school name and reason for cancellation.</li>
      <li>Approvals happen within 2 business days; the refund hits your bank in 5–7 working days.</li>
    </ol>
    <h2>6. Non-refundable items</h2>
    <ul>
      <li>SMS / WhatsApp message costs already consumed.</li>
      <li>Custom development work explicitly scoped and signed off.</li>
      <li>Add-on hardware (ID card printers etc.) once dispatched.</li>
    </ul>
    <p className="muted">We're a small team of parents and ex-educators. If something feels off, write to us — we'd rather make it right than argue over policy.</p>
  </LegalShell>;
}

export function AboutPage() {
  const b = useBusiness(); const owner = b.legal_name && b.legal_name !== "FeeMitra" ? b.legal_name : "our company";
  return <LegalShell title="About FeeMitra" lastUpdated="June 2026">
    <p data-testid="about-owner">FeeMitra is a school fee management software (SaaS) product owned and operated by <b>{owner}</b>{b.address ? `, ${b.address}${b.state ? `, ${b.state}` : ""}` : ""}.</p>
    <h2>What we do</h2>
    <p>FeeMitra helps Indian schools manage students, fee structures, online and offline fee collection, receipts, reminders (SMS, WhatsApp, email), reports and a parent portal.</p>
    <h2>Messages we send</h2>
    <p>On behalf of schools using FeeMitra, we send transactional messages to parents: fee due reminders, payment receipts, payment failure alerts and login (OTP) codes. Parents receive messages only for the school where their child is enrolled.</p>
    <h2>Business details</h2>
    <ul>
      <li>Legal name: {b.legal_name || "—"}</li>
      {b.gstin && <li>GSTIN: {b.gstin}</li>}
      <li>Brand / product: FeeMitra</li>
      <li>Website: myfeemitraapp.in</li>
    </ul>
    <p>Contact us any time from the <a href="/contact">Contact</a> page.</p>
  </LegalShell>;
}

export function ContactPage() {
  const b = useBusiness();
  return <LegalShell title="Contact Us" lastUpdated="June 2026">
    <p>We usually reply within one working day (Mon–Sat, 10 AM – 7 PM IST).</p>
    <ul data-testid="contact-details">
      <li><b>Business:</b> {b.legal_name || "FeeMitra"} (FeeMitra)</li>
      {b.address && <li><b>Address:</b> {b.address}{b.state ? `, ${b.state}` : ""}</li>}
      {b.phone && <li><b>Phone / WhatsApp:</b> <a href={`tel:${b.phone}`}>{b.phone}</a></li>}
      {b.email && <li><b>Email:</b> <a href={`mailto:${b.email}`}>{b.email}</a></li>}
      {b.gstin && <li><b>GSTIN:</b> {b.gstin}</li>}
    </ul>
    <p>Want to try FeeMitra? <a href="/free-trial">Start a free trial</a>.</p>
  </LegalShell>;
}
